import {
  AmountField,
  currencyName,
  DatePick,
  Dialog,
  InvalidInput,
  Pick,
  purposes,
  toIso,
  type Plan,
  type Purpose,
  type TripInput,
} from '@trippath/shared';
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { problemKey } from '../../lib/format';

const currencies = ['EUR', 'USD', 'GBP', 'SEK', 'NOK', 'DKK', 'CHF', 'JPY'];

interface Props {
  title: string;
  submitLabel: string;
  /** The trip as it is, when editing; a new trip starts empty in euros. */
  initial?: TripInput;
  onClose: () => void;
  save: (input: TripInput) => Promise<Plan>;
  onSaved: (plan: Plan) => void;
}

/** Creates or edits a trip: name, what it is for, first day, budget, and — for a new trip — the
 * currency. */
export function TripDialog({ title, submitLabel, initial, onClose, save, onSaved }: Props) {
  const { t, i18n } = useTranslation();
  const nameId = useId();
  const first = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(initial?.name ?? '');
  const [start, setStart] = useState<string | null>(initial?.start ?? null);
  const [budget, setBudget] = useState<number | null>(initial?.budget ?? null);
  const [currency, setCurrency] = useState(initial?.currency ?? 'EUR');
  const [purpose, setPurpose] = useState<Purpose | null>(initial?.purpose ?? null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failed, setFailed] = useState(false);
  // A trip starts today or later, unless it is said to have happened already: an old plan, kept
  // to compare with; one that started before today is such a trip.
  const [today] = useState(() => toIso(new Date()));
  const [past, setPast] = useState(initial !== undefined && initial.start < today);

  const submit = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    const missing: Record<string, string> = {};
    if (name.trim() === '') missing.name = t('problem.required');
    if (start === null) missing.start = t('problem.required');
    if (budget === null) missing.budget = t('problem.required');
    setErrors(missing);
    setFailed(false);
    if (start === null || budget === null || Object.keys(missing).length > 0) {
      return;
    }
    save({ name, start, budget, currency, purpose }).then(onSaved, (error: unknown) => {
      if (error instanceof InvalidInput) {
        setErrors(
          Object.fromEntries(error.fields.map((f) => [f.field, t(problemKey(f.problem))])),
        );
      } else {
        setFailed(true);
      }
    });
  };

  return (
    <Dialog open onClose={onClose} title={title} initialFocus={first}>
      <form className="form" onSubmit={submit} noValidate>
        <div className="field">
          <label className="field-label" htmlFor={nameId} data-required>
            {t('tripForm.name')}
          </label>
          <input
            ref={first}
            id={nameId}
            className="text-input"
            type="text"
            value={name}
            aria-invalid={errors.name !== undefined}
            aria-describedby={errors.name === undefined ? undefined : `${nameId}-error`}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
          {errors.name !== undefined && (
            <p className="field-error" id={`${nameId}-error`}>
              {errors.name}
            </p>
          )}
        </div>
        <Pick
          label={t('tripForm.purpose')}
          options={purposes.map((each) => ({
            value: each,
            label: t(`purpose.${each}`),
            hint: t(`purpose.hint.${each}`),
          }))}
          value={purpose}
          onChange={setPurpose}
          onClear={() => {
            setPurpose(null);
          }}
          placeholder={t('tripForm.purposeNone')}
        />
        <DatePick
          label={t('tripForm.start')}
          required
          value={start}
          onChange={setStart}
          {...(past ? {} : { from: today })}
        />
        {errors.start !== undefined && <p className="field-error">{errors.start}</p>}
        <label className="check">
          <input
            type="checkbox"
            checked={past}
            onChange={(event) => {
              setPast(event.target.checked);
              // Back to an upcoming trip: a day before today is no longer one to start on.
              if (!event.target.checked && start !== null && start < today) setStart(null);
            }}
          />
          {t('tripForm.past')}
        </label>
        <AmountField
          label={t('tripForm.budget')}
          required
          value={budget}
          onChange={setBudget}
          currency={currency}
          error={errors.budget}
        />
        {initial === undefined && (
          <Pick
            label={t('tripForm.currency')}
            options={currencies.map((code) => ({
              value: code,
              label: `${currencyName(code, i18n.language)} (${code})`,
            }))}
            value={currency}
            onChange={setCurrency}
          />
        )}
        {failed && (
          <p className="form-failed" role="alert">
            {t('plan.failed')}
          </p>
        )}
        <div className="dialog-actions">
          <button type="button" className="button" onClick={onClose}>
            {t('dialog.cancel')}
          </button>
          <button type="submit" className="button button-primary">
            {submitLabel}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
